// src/common.ts
import { z } from "zod";
var uuid = z.uuid();
var cents = z.number().int().min(0).max(1e10);
var signedCents = z.number().int().min(-1e10).max(1e10);
var timeHHmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Hor\xE1rio inv\xE1lido (HH:mm)");
var isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Data inv\xE1lida (AAAA-MM-DD)");
var isoDateTime = z.iso.datetime({ offset: true });
var onlyDigits = (v) => v.replace(/\D/g, "");
var phoneBR = z.string().transform(onlyDigits).pipe(z.string().regex(/^\d{10,11}$/, "Telefone inv\xE1lido"));
var cepBR = z.string().transform(onlyDigits).pipe(z.string().regex(/^\d{8}$/, "CEP inv\xE1lido"));
function isValidCpf(raw) {
  const cpf = onlyDigits(raw);
  if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;
  const calc = (len) => {
    let sum = 0;
    for (let i = 0; i < len; i++) sum += Number(cpf[i]) * (len + 1 - i);
    const r = sum * 10 % 11;
    return r === 10 ? 0 : r;
  };
  return calc(9) === Number(cpf[9]) && calc(10) === Number(cpf[10]);
}
function isValidCnpj(raw) {
  const cnpj = onlyDigits(raw);
  if (cnpj.length !== 14 || /^(\d)\1+$/.test(cnpj)) return false;
  const calc = (len) => {
    const weights = len === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const sum = weights.reduce((acc, w, i) => acc + w * Number(cnpj[i]), 0);
    const r = sum % 11;
    return r < 2 ? 0 : 11 - r;
  };
  return calc(12) === Number(cnpj[12]) && calc(13) === Number(cnpj[13]);
}
var cpfBR = z.string().transform(onlyDigits).refine(isValidCpf, "CPF inv\xE1lido");
var cnpjBR = z.string().transform(onlyDigits).refine(isValidCnpj, "CNPJ inv\xE1lido");
var optionalText = (max = 500) => z.string().trim().max(max).optional().nullable().transform((v) => v ? v : null);
var safeUrl = z.string().trim().max(2048).refine((v) => {
  if (v.startsWith("/") && !v.startsWith("//")) return true;
  if (v.startsWith("#")) return true;
  try {
    const u = new URL(v);
    return ["http:", "https:", "mailto:", "tel:"].includes(u.protocol);
  } catch {
    return false;
  }
}, "URL n\xE3o permitida");
var sortOrder = z.enum(["asc", "desc"]).default("asc");
var paginationQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(5).max(100).default(20),
  q: z.string().trim().max(120).optional(),
  sort: z.string().max(40).optional(),
  order: sortOrder
});
var idempotencyKeyHeader = "idempotency-key";
var idempotencyKey = z.string().min(8).max(100);
var addressSchema = z.object({
  cep: cepBR,
  state: z.string().trim().length(2).toUpperCase(),
  city: z.string().trim().min(1).max(120),
  district: z.string().trim().min(1).max(120),
  street: z.string().trim().min(1).max(200),
  number: z.string().trim().min(1).max(20),
  complement: optionalText(120)
});

// src/permissions.ts
var PERMISSIONS = {
  "agenda.read": "Ver agenda pr\xF3pria",
  "agenda.read_all": "Ver agenda de todos os profissionais",
  "agenda.write": "Criar e alterar agendamentos",
  "agenda.block": "Criar bloqueios de agenda",
  "customers.read": "Ver clientes",
  "customers.write": "Cadastrar e editar clientes",
  "customers.export": "Exportar clientes",
  "loyalty.manage": "Gerenciar fidelidade, planos e cupons",
  "campaigns.manage": "Criar e enviar campanhas",
  "cash.operate": "Operar caixa e registrar recebimentos",
  "cash.manage": "Ajustes, sangrias e fechamento de caixa",
  "expenses.manage": "Lan\xE7ar despesas",
  "commissions.read": "Ver comiss\xF5es pr\xF3prias",
  "commissions.read_all": "Ver comiss\xF5es de todos",
  "commissions.manage": "Configurar regras de comiss\xE3o",
  "reports.read": "Ver relat\xF3rios",
  "catalog.manage": "Gerenciar servi\xE7os e profissionais",
  "settings.manage": "Configura\xE7\xF5es da barbearia",
  "members.manage": "Gerenciar membros, pap\xE9is e convites",
  "site.edit": "Editar site",
  "site.publish": "Publicar site",
  "ai.use": "Usar assistente de IA",
  "audit.read": "Ver auditoria"
};
var ALL_PERMISSIONS = Object.keys(PERMISSIONS);
var DEFAULT_ROLES = {
  owner: { name: "Propriet\xE1rio", permissions: ALL_PERMISSIONS },
  manager: {
    name: "Gerente",
    permissions: ALL_PERMISSIONS.filter((p) => p !== "members.manage" && p !== "audit.read")
  },
  receptionist: {
    name: "Recepcionista",
    permissions: [
      "agenda.read",
      "agenda.read_all",
      "agenda.write",
      "agenda.block",
      "customers.read",
      "customers.write",
      "cash.operate",
      "ai.use"
    ]
  },
  barber: {
    name: "Barbeiro",
    permissions: ["agenda.read", "agenda.write", "customers.read", "commissions.read", "ai.use"]
  }
};

// src/legal.ts
import { z as z2 } from "zod";
var LEGAL_VERSION = "2026-10-22";
var acceptTermsInput = z2.object({ version: z2.string().min(1).max(20) });
var acceptTermsFlag = z2.literal(true, { error: "\xC9 preciso aceitar os Termos de Uso e a Pol\xEDtica de Privacidade" });

// src/auth.ts
import { z as z3 } from "zod";
var passwordSchema = z3.string().min(8, "M\xEDnimo de 8 caracteres").max(128).regex(/[A-Za-z]/, "Inclua ao menos uma letra").regex(/\d/, "Inclua ao menos um n\xFAmero");
var emailSchema = z3.email("E-mail inv\xE1lido").trim().toLowerCase().max(254);
var loginInput = z3.object({ email: emailSchema, password: z3.string().min(1).max(128) });
var registerInput = z3.object({
  name: z3.string().trim().min(2).max(120),
  email: emailSchema,
  password: passwordSchema,
  organizationName: z3.string().trim().min(2).max(120),
  timezone: z3.string().default("America/Sao_Paulo"),
  acceptTerms: acceptTermsFlag
});
var forgotPasswordInput = z3.object({ email: emailSchema });
var resetPasswordInput = z3.object({ token: z3.string().min(20).max(200), password: passwordSchema });
var acceptInviteInput = z3.object({
  token: z3.string().min(20).max(200),
  name: z3.string().trim().min(2).max(120).optional(),
  password: passwordSchema.optional(),
  acceptTerms: acceptTermsFlag
});
var changePasswordInput = z3.object({ currentPassword: z3.string().min(1), newPassword: passwordSchema });
var updateProfileInput = z3.object({
  name: z3.string().trim().min(2, "Informe seu nome").max(120),
  email: emailSchema,
  currentPassword: z3.string().max(128).optional()
}).strict();
var inviteMemberInput = z3.object({
  email: emailSchema,
  roleId: z3.uuid(),
  barberId: z3.uuid().optional().nullable()
});
var updateMemberInput = z3.object({
  roleId: z3.uuid().optional(),
  barberId: z3.uuid().nullable().optional(),
  active: z3.boolean().optional()
});
var roleInput = z3.object({
  name: z3.string().trim().min(2).max(60),
  permissions: z3.array(z3.string()).max(100),
  description: optionalText(200)
});

// src/org.ts
import { z as z4 } from "zod";
var slugSchema = z4.string().trim().toLowerCase().min(3).max(60).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Use letras min\xFAsculas, n\xFAmeros e h\xEDfens");
var organizationSettingsInput = z4.object({
  name: z4.string().trim().min(2).max(120),
  slug: slugSchema,
  cnpj: cnpjBR.optional().nullable(),
  phone: phoneBR.optional().nullable(),
  whatsapp: phoneBR.optional().nullable(),
  email: z4.email().optional().nullable(),
  timezone: z4.string().min(3).max(60),
  instagram: safeUrl.optional().nullable(),
  facebook: safeUrl.optional().nullable(),
  tiktok: safeUrl.optional().nullable(),
  brandPrimary: z4.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
  brandAccent: z4.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
  logoAssetId: z4.uuid().nullable().optional(),
  faviconAssetId: z4.uuid().nullable().optional()
});
var branchInput = z4.object({
  name: z4.string().trim().min(2).max(120),
  phone: phoneBR.optional().nullable(),
  address: addressSchema,
  active: z4.boolean().default(true)
});
var chairInput = z4.object({
  branchId: z4.uuid(),
  name: z4.string().trim().min(1).max(60),
  active: z4.boolean().default(true)
});
var weekday = z4.number().int().min(0).max(6);
var businessHoursInterval = z4.object({ weekday, opensAt: timeHHmm, closesAt: timeHHmm }).refine((v) => v.opensAt < v.closesAt, "Abertura deve ser antes do fechamento");
var businessHoursInput = z4.object({
  branchId: z4.uuid(),
  barberId: z4.uuid().nullable().optional(),
  intervals: z4.array(businessHoursInterval).max(50)
});
var holidayInput = z4.object({
  branchId: z4.uuid().nullable().optional(),
  date: isoDate,
  name: z4.string().trim().min(2).max(120)
});
var bookingPolicyInput = z4.object({
  slotIntervalMinutes: z4.number().int().refine((v) => [5, 10, 15, 20, 30, 60].includes(v)),
  minAdvanceMinutes: z4.number().int().min(0).max(60 * 24 * 7),
  maxAdvanceDays: z4.number().int().min(1).max(365),
  cancellationMinNoticeMinutes: z4.number().int().min(0).max(60 * 24 * 7),
  lateToleranceMinutes: z4.number().int().min(0).max(120),
  bufferMinutes: z4.number().int().min(0).max(120),
  publicBookingRequiresConfirmation: z4.boolean(),
  allowPublicBooking: z4.boolean()
});
var cepLookupResponse = z4.object({
  cep: z4.string(),
  state: z4.string(),
  city: z4.string(),
  district: z4.string(),
  street: z4.string()
});
var onboardingStatus = z4.object({
  hasBranch: z4.boolean(),
  hasHours: z4.boolean(),
  hasServices: z4.boolean(),
  hasBarbers: z4.boolean(),
  sitePublished: z4.boolean()
});

// src/catalog.ts
import { z as z5 } from "zod";
var serviceCategoryInput = z5.object({
  name: z5.string().trim().min(2).max(60),
  position: z5.number().int().min(0).default(0)
});
var serviceInput = z5.object({
  name: z5.string().trim().min(2).max(120),
  description: optionalText(1e3),
  categoryId: z5.uuid().nullable().optional(),
  durationMinutes: z5.number().int().min(5).max(480),
  priceCents: cents,
  active: z5.boolean().default(true),
  publicVisible: z5.boolean().default(true),
  imageAssetId: z5.uuid().nullable().optional()
});
var serviceListQuery = paginationQuery.extend({
  categoryId: z5.uuid().optional(),
  active: z5.enum(["true", "false"]).optional(),
  includeDeleted: z5.enum(["true", "false"]).optional()
});
var barberInput = z5.object({
  displayName: z5.string().trim().min(2).max(120),
  bio: optionalText(1e3),
  specialties: z5.array(z5.string().trim().min(1).max(40)).max(12).default([]),
  photoAssetId: z5.uuid().nullable().optional(),
  branchId: z5.uuid(),
  chairId: z5.uuid().nullable().optional(),
  active: z5.boolean().default(true),
  publicVisible: z5.boolean().default(true),
  monthlyGoalCents: cents.nullable().optional(),
  color: z5.string().regex(/^#[0-9a-fA-F]{6}$/).default("#8a6a3b")
});
var barberServicesInput = z5.object({
  items: z5.array(
    z5.object({
      serviceId: z5.uuid(),
      priceCentsOverride: cents.nullable().optional(),
      durationMinutesOverride: z5.number().int().min(5).max(480).nullable().optional()
    })
  ).max(200)
});

// src/agenda.ts
import { z as z6 } from "zod";
var APPOINTMENT_STATUSES = [
  "requested",
  "confirmed",
  "in_progress",
  "completed",
  "cancelled",
  "no_show"
];
var appointmentStatus = z6.enum(APPOINTMENT_STATUSES);
var BLOCKING_STATUSES = ["requested", "confirmed", "in_progress"];
var STATUS_LABELS = {
  requested: "Solicitado",
  confirmed: "Confirmado",
  in_progress: "Em atendimento",
  completed: "Conclu\xEDdo",
  cancelled: "Cancelado",
  no_show: "Aus\xEAncia"
};
var APPOINTMENT_TRANSITIONS = {
  requested: ["confirmed", "cancelled"],
  confirmed: ["in_progress", "cancelled", "no_show"],
  in_progress: ["completed"],
  completed: [],
  cancelled: [],
  no_show: []
};
function canTransition(from, to) {
  return APPOINTMENT_TRANSITIONS[from].includes(to);
}
var appointmentSource = z6.enum(["internal", "public", "ai_suggested"]);
var availabilityQuery = z6.object({
  branchId: z6.uuid().optional(),
  serviceId: z6.uuid(),
  barberId: z6.uuid().optional(),
  date: isoDate
});
var createAppointmentInput = z6.object({
  branchId: z6.uuid(),
  barberId: z6.uuid(),
  serviceId: z6.uuid(),
  customerId: z6.uuid(),
  startsAt: isoDateTime,
  notes: optionalText(1e3),
  status: z6.enum(["requested", "confirmed"]).default("confirmed"),
  fromWaitlistId: z6.uuid().optional()
});
var rescheduleAppointmentInput = z6.object({
  startsAt: isoDateTime,
  barberId: z6.uuid().optional(),
  reason: optionalText(300)
});
var changeStatusInput = z6.object({
  status: appointmentStatus,
  reason: optionalText(300)
});
var appointmentListQuery = paginationQuery.extend({
  from: isoDateTime,
  to: isoDateTime,
  branchId: z6.uuid().optional(),
  barberId: z6.uuid().optional(),
  customerId: z6.uuid().optional(),
  status: z6.union([appointmentStatus, z6.array(appointmentStatus)]).optional()
});
var scheduleBlockInput = z6.object({
  branchId: z6.uuid(),
  barberId: z6.uuid().nullable().optional(),
  startsAt: isoDateTime,
  endsAt: isoDateTime,
  reason: z6.string().trim().min(2).max(200)
}).refine((v) => v.startsAt < v.endsAt, "Fim deve ser ap\xF3s o in\xEDcio");
var waitlistInput = z6.object({
  customerId: z6.uuid(),
  serviceId: z6.uuid(),
  barberId: z6.uuid().nullable().optional(),
  branchId: z6.uuid(),
  desiredDate: isoDate,
  preferredPeriod: z6.enum(["any", "morning", "afternoon", "evening"]).default("any"),
  notes: optionalText(300)
});
var publicBookingInput = z6.object({
  serviceId: z6.uuid(),
  barberId: z6.uuid(),
  branchId: z6.uuid(),
  startsAt: isoDateTime,
  customer: z6.object({
    name: z6.string().trim().min(2).max(120),
    phone: phoneBR,
    email: emailSchema,
    marketingConsent: z6.boolean().default(false)
  }),
  notes: optionalText(500),
  idempotencyKey,
  /** honeypot anti-bot: precisa vir vazio */
  website: z6.string().max(0).optional()
});
var calendarQuery = z6.object({
  from: isoDateTime,
  to: isoDateTime,
  branchId: z6.uuid().optional(),
  barberId: z6.uuid().optional(),
  status: z6.union([appointmentStatus, z6.array(appointmentStatus)]).optional()
});
var scheduleBlockListQuery = z6.object({
  from: isoDateTime,
  to: isoDateTime,
  branchId: z6.uuid().optional(),
  barberId: z6.uuid().optional()
});
var waitlistListQuery = z6.object({
  date: isoDate.optional(),
  branchId: z6.uuid().optional(),
  status: z6.enum(["waiting", "notified", "booked", "cancelled"]).optional()
});
var waitlistStatusInput = z6.object({
  status: z6.enum(["waiting", "notified", "cancelled"])
});
var publicCancelInput = z6.object({ reason: optionalText(300) });
var publicAvailabilityQuery = z6.object({
  serviceId: z6.uuid(),
  barberId: z6.uuid().optional(),
  branchId: z6.uuid().optional(),
  date: isoDate
});
var internalAvailabilityQuery = availabilityQuery.extend({
  excludeAppointmentId: z6.uuid().optional()
});

// src/customers.ts
import { z as z7 } from "zod";
var customerInput = z7.object({
  name: z7.string().trim().min(2).max(120),
  phone: phoneBR.optional().nullable(),
  email: emailSchema.optional().nullable(),
  cpf: cpfBR.optional().nullable(),
  birthDate: isoDate.optional().nullable(),
  preferences: optionalText(1e3),
  notes: optionalText(2e3),
  preferredBarberId: z7.uuid().nullable().optional(),
  marketingConsent: z7.boolean().default(false),
  /** true = nunca enviar mensagens de WhatsApp a este cliente */
  whatsappOptOut: z7.boolean().default(false),
  tags: z7.array(z7.string().trim().min(1).max(30)).max(10).default([])
});
var customerListQuery = paginationQuery.extend({
  inactiveDays: z7.coerce.number().int().min(1).max(3650).optional(),
  barberId: z7.uuid().optional(),
  marketingConsent: z7.enum(["true", "false"]).optional(),
  includeDeleted: z7.enum(["true", "false"]).optional()
});
var loyaltyProgramInput = z7.object({
  mode: z7.enum(["points", "visits"]),
  active: z7.boolean(),
  /** points: pontos por real gasto; visits: 1 por visita */
  pointsPerReal: z7.number().int().min(0).max(100).default(1),
  rewardThreshold: z7.number().int().min(1).max(1e5),
  rewardDescription: z7.string().trim().min(2).max(200),
  rewardValueCents: cents.default(0)
});
var loyaltyAdjustInput = z7.object({
  points: z7.number().int().min(-1e5).max(1e5).refine((v) => v !== 0),
  reason: z7.string().trim().min(3).max(200)
});
var subscriptionPlanInput = z7.object({
  name: z7.string().trim().min(2).max(120),
  description: optionalText(1e3),
  kind: z7.enum(["recurring", "package"]),
  priceCents: cents,
  /** recorrente: intervalo em meses; pacote: validade em dias */
  billingIntervalMonths: z7.number().int().min(1).max(12).nullable().optional(),
  validityDays: z7.number().int().min(1).max(730).nullable().optional(),
  /** quantidade de usos incluídos por ciclo/pacote; null = ilimitado no ciclo */
  usesIncluded: z7.number().int().min(1).max(1e3).nullable().optional(),
  serviceIds: z7.array(z7.uuid()).min(1).max(50),
  active: z7.boolean().default(true)
});
var subscribeCustomerInput = z7.object({ planId: z7.uuid(), startsOn: isoDate });
var couponInput = z7.object({
  code: z7.string().trim().toUpperCase().min(3).max(30).regex(/^[A-Z0-9_-]+$/),
  description: optionalText(200),
  discountType: z7.enum(["percent", "fixed"]),
  discountValue: z7.number().int().min(1),
  maxRedemptions: z7.number().int().min(1).nullable().optional(),
  maxPerCustomer: z7.number().int().min(1).default(1),
  validFrom: isoDate,
  validUntil: isoDate,
  minAmountCents: cents.default(0),
  serviceIds: z7.array(z7.uuid()).max(50).default([]),
  active: z7.boolean().default(true)
}).refine((v) => v.validFrom <= v.validUntil, "Per\xEDodo inv\xE1lido").refine((v) => v.discountType !== "percent" || v.discountValue <= 100, "Percentual m\xE1ximo \xE9 100");
var campaignInput = z7.object({
  name: z7.string().trim().min(2).max(120),
  subject: z7.string().trim().min(2).max(150),
  body: z7.string().trim().min(10).max(5e3),
  audience: z7.object({
    inactiveDays: z7.number().int().min(1).max(3650).nullable().optional(),
    barberId: z7.uuid().nullable().optional(),
    tags: z7.array(z7.string()).max(10).default([])
  }),
  couponId: z7.uuid().nullable().optional()
});
var customerInactiveQuery = z7.object({
  days: z7.coerce.number().int().min(1).max(3650).default(45),
  limit: z7.coerce.number().int().min(1).max(500).default(100)
});
var redeemRewardInput = z7.object({ note: z7.string().trim().max(200).optional() });
var cancelSubscriptionInput = z7.object({ reason: z7.string().trim().min(3).max(200) });
var couponListQuery = paginationQuery.extend({
  active: z7.enum(["true", "false"]).optional()
});

// src/finance.ts
import { z as z8 } from "zod";
var PAYMENT_KINDS = ["cash", "pix", "credit_card", "debit_card", "other"];
var paymentKind = z8.enum(PAYMENT_KINDS);
var paymentMethodInput = z8.object({
  name: z8.string().trim().min(2).max(60),
  kind: paymentKind,
  feePercentBps: z8.number().int().min(0).max(1e4).default(0),
  active: z8.boolean().default(true)
});
var openCashRegisterInput = z8.object({
  branchId: z8.uuid(),
  openingAmountCents: cents,
  notes: optionalText(300)
});
var closeCashRegisterInput = z8.object({
  countedAmountCents: cents,
  notes: optionalText(500)
});
var cashMovementInput = z8.object({
  type: z8.enum(["withdrawal", "deposit", "adjustment"]),
  amountCents: z8.number().int().min(-1e8).max(1e8).refine((v) => v !== 0),
  reason: z8.string().trim().min(3).max(300)
});
var paymentInput = z8.object({
  idempotencyKey,
  appointmentId: z8.uuid().nullable().optional(),
  customerId: z8.uuid().nullable().optional(),
  subscriptionId: z8.uuid().nullable().optional(),
  cashRegisterId: z8.uuid(),
  items: z8.array(z8.object({ paymentMethodId: z8.uuid(), amountCents: cents.refine((v) => v > 0) })).max(5),
  couponCode: z8.string().trim().toUpperCase().max(30).optional(),
  usePackageSubscriptionId: z8.uuid().optional(),
  notes: optionalText(300)
});
var refundInput = z8.object({ reason: z8.string().trim().min(3).max(300) });
var expenseInput = z8.object({
  branchId: z8.uuid().nullable().optional(),
  category: z8.string().trim().min(2).max(60),
  description: z8.string().trim().min(2).max(300),
  amountCents: cents.refine((v) => v > 0),
  dueDate: isoDate,
  paidAt: isoDate.nullable().optional(),
  cashRegisterId: z8.uuid().nullable().optional()
});
var commissionRuleInput = z8.object({
  barberId: z8.uuid().nullable().optional(),
  serviceId: z8.uuid().nullable().optional(),
  type: z8.enum(["percent", "fixed"]),
  /** percent: basis points (5000 = 50%); fixed: centavos */
  value: z8.number().int().min(0).max(1e6),
  priority: z8.number().int().min(0).max(100).default(0),
  active: z8.boolean().default(true)
});
var reportQuery = z8.object({
  from: isoDate,
  to: isoDate,
  branchId: z8.uuid().optional(),
  barberId: z8.uuid().optional()
});
var paymentListQuery = paginationQuery.extend({
  from: isoDate.optional(),
  to: isoDate.optional(),
  cashRegisterId: z8.uuid().optional(),
  status: z8.enum(["confirmed", "refunded"]).optional(),
  paymentMethodId: z8.uuid().optional(),
  branchId: z8.uuid().optional()
});
var pendingPaymentsQuery = paginationQuery.extend({
  branchId: z8.uuid().optional(),
  barberId: z8.uuid().optional()
});
var paymentQuoteInput = z8.object({
  appointmentId: z8.uuid().nullable().optional(),
  subscriptionId: z8.uuid().nullable().optional(),
  couponCode: z8.string().trim().toUpperCase().max(30).optional(),
  usePackageSubscriptionId: z8.uuid().optional()
});
var cashRegisterListQuery = paginationQuery.extend({
  branchId: z8.uuid().optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
  status: z8.enum(["open", "closed"]).optional()
});
var cashRegisterExportQuery = cashRegisterListQuery.omit({ page: true, pageSize: true }).extend({
  format: z8.enum(["xlsx", "pdf"]).default("xlsx")
});
var expenseListQuery = paginationQuery.extend({
  from: isoDate.optional(),
  to: isoDate.optional(),
  category: z8.string().trim().max(60).optional(),
  branchId: z8.uuid().optional(),
  /** paid | unpaid | cancelled | all (padrão: sem canceladas) */
  status: z8.enum(["paid", "unpaid", "cancelled", "all"]).optional()
});
var commissionListQuery = paginationQuery.extend({
  from: isoDate.optional(),
  to: isoDate.optional(),
  barberId: z8.uuid().optional(),
  status: z8.enum(["pending", "paid", "reversed"]).optional()
});
var payCommissionsInput = z8.object({ ids: z8.array(z8.uuid()).min(1).max(500) });

// src/site.ts
import { z as z9 } from "zod";
var text = (max) => z9.string().trim().max(max);
var assetRef = z9.object({ assetId: z9.uuid().nullable().optional(), url: safeUrl.nullable().optional(), alt: text(200).default("") });
var cta = z9.object({ label: text(40).min(1), href: safeUrl });
var base = { id: z9.string().min(1).max(40), hidden: z9.boolean().default(false) };
var heroBlock = z9.object({
  ...base,
  type: z9.literal("hero"),
  eyebrow: text(60).default(""),
  title: text(120).min(1),
  subtitle: text(300).default(""),
  image: assetRef.optional(),
  primaryCta: cta.optional(),
  secondaryCta: cta.optional()
});
var servicesBlock = z9.object({
  ...base,
  type: z9.literal("services"),
  title: text(120).default("Servi\xE7os"),
  subtitle: text(300).default(""),
  showPrices: z9.boolean().default(true),
  serviceIds: z9.array(z9.uuid()).max(100).default([])
});
var barbersBlock = z9.object({
  ...base,
  type: z9.literal("barbers"),
  title: text(120).default("Nossa equipe"),
  subtitle: text(300).default(""),
  barberIds: z9.array(z9.uuid()).max(50).default([])
});
var galleryBlock = z9.object({
  ...base,
  type: z9.literal("gallery"),
  title: text(120).default("Galeria"),
  images: z9.array(assetRef).max(24).default([])
});
var reviewsBlock = z9.object({
  ...base,
  type: z9.literal("reviews"),
  title: text(120).default("O que dizem nossos clientes"),
  /** só avaliações com autorização de publicação */
  maxItems: z9.number().int().min(1).max(12).default(6)
});
var locationBlock = z9.object({
  ...base,
  type: z9.literal("location"),
  title: text(120).default("Onde estamos"),
  branchId: z9.uuid().nullable().optional(),
  showHours: z9.boolean().default(true),
  showMap: z9.boolean().default(true)
});
var ctaBlock = z9.object({
  ...base,
  type: z9.literal("cta"),
  title: text(120).min(1),
  body: text(500).default(""),
  button: cta
});
var textBlock = z9.object({
  ...base,
  type: z9.literal("text"),
  title: text(120).default(""),
  /** texto puro; quebras de linha viram parágrafos. Sem HTML. */
  body: text(5e3).default("")
});
var faqBlock = z9.object({
  ...base,
  type: z9.literal("faq"),
  title: text(120).default("Perguntas frequentes"),
  items: z9.array(z9.object({ q: text(200).min(1), a: text(1e3).min(1) })).max(20).default([])
});
var contactFormBlock = z9.object({
  ...base,
  type: z9.literal("contact_form"),
  title: text(120).default("Fale com a gente"),
  subtitle: text(300).default(""),
  successMessage: text(300).default("Mensagem enviada. Retornaremos em breve."),
  successPageSlug: slugSchema.nullable().optional()
});
var siteBlock = z9.discriminatedUnion("type", [
  heroBlock,
  servicesBlock,
  barbersBlock,
  galleryBlock,
  reviewsBlock,
  locationBlock,
  ctaBlock,
  textBlock,
  faqBlock,
  contactFormBlock
]);
var seoSchema = z9.object({
  title: text(70).default(""),
  description: text(160).default(""),
  ogImage: assetRef.nullable().optional(),
  noindex: z9.boolean().default(false)
});
var pageContentSchema = z9.object({
  blocks: z9.array(siteBlock).max(40),
  seo: seoSchema
});
var pageSlug = z9.union([z9.literal(""), slugSchema]);
var createPageInput = z9.object({ title: text(120).min(1), slug: pageSlug, showInNav: z9.boolean().default(true) });
var updatePageMetaInput = createPageInput.partial().extend({ position: z9.number().int().min(0).optional() });
var saveDraftInput = z9.object({ content: pageContentSchema, baseVersion: z9.number().int().min(0) });
var siteThemeInput = z9.object({
  primary: z9.string().regex(/^#[0-9a-fA-F]{6}$/),
  accent: z9.string().regex(/^#[0-9a-fA-F]{6}$/),
  background: z9.string().regex(/^#[0-9a-fA-F]{6}$/),
  fontPair: z9.enum(["classic", "modern", "editorial"]),
  logoAssetId: z9.uuid().nullable().optional(),
  faviconAssetId: z9.uuid().nullable().optional()
});
var contactMessageInput = z9.object({
  name: z9.string().trim().min(2).max(120),
  email: z9.email(),
  phone: z9.string().trim().max(20).optional(),
  message: z9.string().trim().min(5).max(2e3),
  pageSlug,
  website: z9.string().max(0).optional()
});
var reviewInput = z9.object({
  customerName: z9.string().trim().min(2).max(80),
  rating: z9.number().int().min(1).max(5),
  comment: z9.string().trim().min(3).max(600),
  publicationAuthorized: z9.boolean()
});
var messagesQuery = z9.object({
  page: z9.coerce.number().int().min(1).default(1),
  pageSize: z9.coerce.number().int().min(5).max(100).default(20),
  unread: z9.enum(["true", "false"]).optional()
});
var reviewsQuery = z9.object({
  page: z9.coerce.number().int().min(1).default(1),
  pageSize: z9.coerce.number().int().min(5).max(100).default(20)
});
var reviewPatchInput = reviewInput.partial();
var RESERVED_PAGE_SLUGS = ["agendar", "agendamento"];

// src/ai.ts
import { z as z10 } from "zod";

// src/notifications.ts
var NOTIFICATION_TYPES = [
  "invite",
  "password_reset",
  "appointment_confirmation",
  "appointment_requested",
  "appointment_rescheduled",
  "appointment_cancelled",
  "appointment_reminder",
  "financial_alert",
  "campaign",
  "contact_message"
];
var NOTIFICATION_STATUSES = [
  "queued",
  "sent",
  "failed",
  "skipped_unconfigured",
  "skipped_optout",
  "skipped_invalid_phone",
  "skipped_disabled",
  "skipped_limit"
];
var NOTIFICATION_CHANNELS = ["email", "whatsapp"];
var EMAIL_UNAVAILABLE_MESSAGE = "O envio de e-mails n\xE3o est\xE1 dispon\xEDvel no momento. Fale com o suporte.";
var AI_UNAVAILABLE_MESSAGE = "O assistente de IA n\xE3o est\xE1 dispon\xEDvel no momento. Fale com o suporte.";
var WHATSAPP_UNAVAILABLE_MESSAGE = "O WhatsApp n\xE3o est\xE1 dispon\xEDvel no momento. Fale com o suporte.";

// src/ai.ts
var aiChatInput = z10.object({
  messages: z10.array(z10.object({ role: z10.enum(["user", "assistant"]), content: z10.string().min(1).max(4e3) })).min(1).max(30)
});
var KPI_METRICS = [
  "revenue",
  "received",
  "expenses",
  "pending",
  "average_ticket",
  "completed_appointments",
  "no_show_rate",
  "top_services",
  "barber_performance",
  "occupancy"
];
var kpiMetric = z10.enum(KPI_METRICS);
var aiDailySummaryQuery = z10.object({ date: z10.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Data inv\xE1lida (AAAA-MM-DD)").optional() });
var publicAssistantInput = z10.object({
  messages: z10.array(z10.object({ role: z10.enum(["user", "assistant"]), content: z10.string().trim().min(1).max(500) })).min(1).max(8)
});
var notificationListQuery = paginationQuery.extend({
  status: z10.enum(NOTIFICATION_STATUSES).optional(),
  type: z10.enum(NOTIFICATION_TYPES).optional()
});

// src/platform.ts
import { z as z11 } from "zod";
var PLATFORM_ROLES = ["owner", "support", "finance", "readonly"];
var PLATFORM_ROLE_LABEL = {
  owner: "Propriet\xE1rio",
  support: "Suporte",
  finance: "Financeiro",
  readonly: "Somente leitura"
};
var PLAN_LIMIT_KEYS = ["users", "units", "appointments_month", "storage_mb", "ai_requests_month"];
var PLAN_LIMIT_LABEL = {
  users: "Usu\xE1rios da equipe",
  units: "Unidades",
  appointments_month: "Agendamentos por m\xEAs",
  storage_mb: "Armazenamento (MB)",
  ai_requests_month: "Requisi\xE7\xF5es de IA por m\xEAs"
};
var FEATURE_KEYS = ["ai_assistant", "campaigns", "loyalty", "public_site", "reports_export"];
var FEATURE_LABEL = {
  ai_assistant: "Assistente de IA",
  campaigns: "Campanhas",
  loyalty: "Fidelidade e planos de clientes",
  public_site: "Site p\xFAblico com agendamento",
  reports_export: "Exporta\xE7\xE3o de relat\xF3rios"
};
var SUBSCRIPTION_STATUSES = ["trialing", "active", "past_due", "grace", "suspended", "canceled"];
var SUBSCRIPTION_STATUS_LABEL = {
  trialing: "Em teste",
  active: "Ativa",
  past_due: "Pagamento atrasado",
  grace: "Car\xEAncia",
  suspended: "Suspensa",
  canceled: "Cancelada"
};
var SUBSCRIPTION_ACTIONS = ["upgrade", "downgrade", "cancel", "reactivate", "trial_start", "trial_convert", "extend_trial", "suspend"];
var ORG_STATUSES = ["active", "suspended", "pending_deletion"];
var ORG_STATUS_LABEL = { active: "Ativa", suspended: "Suspensa", pending_deletion: "Exclus\xE3o agendada" };
var INVOICE_STATUSES = ["pending", "paid", "overdue", "refunded", "canceled"];
var BILLING_EVENT_STATUSES = ["received", "processed", "failed", "ignored"];
var platformListQuery = z11.object({
  page: z11.coerce.number().int().min(1).default(1),
  pageSize: z11.coerce.number().int().min(5).max(200).default(20),
  q: z11.string().trim().max(120).optional(),
  sort: z11.string().max(40).optional(),
  order: z11.enum(["asc", "desc"]).default("asc")
});
var platformOrgsQuery = platformListQuery.extend({
  status: z11.enum(ORG_STATUSES).optional(),
  subscriptionStatus: z11.enum(SUBSCRIPTION_STATUSES).optional(),
  planId: z11.uuid().optional()
});
var platformSubscriptionsQuery = platformListQuery.extend({
  status: z11.enum(SUBSCRIPTION_STATUSES).optional(),
  planId: z11.uuid().optional()
});
var platformEventsQuery = platformListQuery.extend({ status: z11.enum(BILLING_EVENT_STATUSES).optional() });
var platformInvoicesQuery = platformListQuery.extend({ status: z11.enum(INVOICE_STATUSES).optional() });
var platformTicketsQuery = platformListQuery.extend({
  status: z11.enum(["open", "pending", "resolved", "closed"]).optional(),
  priority: z11.enum(["low", "normal", "high", "urgent"]).optional()
});
var platformAuditQuery = platformListQuery.extend({
  action: z11.string().max(80).optional(),
  organizationId: z11.uuid().optional(),
  from: z11.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  to: z11.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional()
});
var platformUsageQuery = platformListQuery.extend({ period: z11.string().regex(/^\d{4}-\d{2}$/).optional() });
var platformMetricsQuery = z11.object({
  from: z11.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  to: z11.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional()
});
var limitValue = z11.number().int().min(0).max(2e9).nullable();
var platformPlanInput = z11.object({
  key: z11.string().trim().toLowerCase().min(2).max(40).regex(/^[a-z0-9_-]+$/, "Use letras min\xFAsculas, n\xFAmeros, h\xEDfen ou sublinhado"),
  name: z11.string().trim().min(2).max(80),
  description: z11.string().trim().max(300).default(""),
  active: z11.boolean().default(true),
  isPublic: z11.boolean().default(true),
  priceCents: z11.number().int().min(0).max(1e8),
  currency: z11.literal("BRL").default("BRL"),
  interval: z11.enum(["month", "year"]).default("month"),
  trialDays: z11.number().int().min(0).max(365).default(0),
  sort: z11.number().int().min(0).max(1e3).default(0),
  limits: z11.partialRecord(z11.enum(PLAN_LIMIT_KEYS), limitValue).default({}),
  features: z11.partialRecord(z11.enum(FEATURE_KEYS), z11.boolean()).default({})
});
var platformFeatureFlagInput = z11.object({
  key: z11.enum(FEATURE_KEYS),
  defaultEnabled: z11.boolean(),
  description: z11.string().trim().max(200).default("")
});
var platformChangePlanInput = z11.object({ planId: z11.uuid(), note: z11.string().trim().max(300).optional() });
var platformExtendTrialInput = z11.object({ days: z11.number().int().min(1).max(365), note: z11.string().trim().max(300).optional() });
var platformCancelSubscriptionInput = z11.object({ immediate: z11.boolean().default(false), note: z11.string().trim().max(300).optional() });
var platformReactivateSubscriptionInput = z11.object({ note: z11.string().trim().max(300).optional() });
var PLATFORM_CREDENTIALS = ["password", "link"];
var PLATFORM_CREDENTIAL_LABEL = { password: "Definir senha agora", link: "Enviar link" };
var PLATFORM_MAX_TRIAL_DAYS = 365;
var credentialFields = {
  credential: z11.enum(PLATFORM_CREDENTIALS),
  password: passwordSchema.optional()
};
var requirePasswordWhenSet = (v, ctx) => {
  if (v.credential === "password" && !v.password) ctx.addIssue({ code: "custom", path: ["password"], message: "Informe a senha" });
};
var platformCreateOrgInput = z11.object({
  name: z11.string().trim().min(2).max(120),
  timezone: z11.string().max(60).default("America/Sao_Paulo"),
  planId: z11.uuid().optional(),
  trialDays: z11.number().int().min(0).max(PLATFORM_MAX_TRIAL_DAYS).optional(),
  owner: z11.object({ name: z11.string().trim().min(2).max(120), email: emailSchema, ...credentialFields }).superRefine(requirePasswordWhenSet)
});
var platformCreateOrgUserInput = z11.object({ name: z11.string().trim().min(2).max(120), email: emailSchema, roleKey: z11.string().trim().min(1).max(30), ...credentialFields }).superRefine(requirePasswordWhenSet);
var platformMemberUpdateInput = z11.object({ roleKey: z11.string().trim().min(1).max(30).optional(), active: z11.boolean().optional() }).refine((v) => v.roleKey !== void 0 || v.active !== void 0, { message: "Nada para alterar", path: ["roleKey"] });
var platformImpersonateInput = z11.object({
  userId: z11.uuid(),
  reason: z11.string().trim().max(300, "M\xE1ximo de 300 caracteres").optional().transform((v) => v ? v : void 0)
});
var IMPERSONATION_TTL_MIN = 30;
var PLATFORM_USER_STATUSES = ["active", "inactive"];
var PLATFORM_USER_STATUS_LABEL = { active: "Ativo", inactive: "Inativo" };
var PLATFORM_USER_SORTS = ["name", "email", "createdAt"];
var platformUsersQuery = platformListQuery.extend({
  pageSize: z11.coerce.number().int().min(5).max(100).default(20),
  sort: z11.enum(PLATFORM_USER_SORTS).optional(),
  organizationId: z11.uuid().optional(),
  /** chave do papel no tenant (owner, manager, receptionist, barber...) */
  role: z11.string().trim().min(1).max(30).optional(),
  status: z11.enum(PLATFORM_USER_STATUSES).optional()
});
var REMOVED_EMAIL_DOMAIN = "removido.invalid";
var platformUserReason = z11.string().trim().min(10, "Descreva o motivo (m\xEDnimo 10 caracteres)").max(300, "M\xE1ximo de 300 caracteres");
var platformUserUpdateInput = z11.object({
  name: z11.string().trim().min(2, "Informe o nome").max(120).optional(),
  email: emailSchema.optional(),
  active: z11.boolean().optional(),
  organizationId: z11.uuid().optional()
}).refine((v) => v.active === void 0 || v.organizationId !== void 0, { message: "Informe a organiza\xE7\xE3o", path: ["organizationId"] }).refine((v) => v.name !== void 0 || v.email !== void 0 || v.active !== void 0, { message: "Nada para alterar", path: ["name"] });
var platformUserSetPasswordInput = z11.object({ password: passwordSchema, reason: platformUserReason });
var platformUserDeleteInput = z11.object({ reason: platformUserReason });
var platformSuspendOrgInput = z11.object({ reason: z11.string().trim().min(3, "Informe o motivo").max(200) });
var platformScheduleDeletionInput = z11.object({
  confirmName: z11.string().trim().min(1),
  windowDays: z11.number().int().min(1).max(365).optional(),
  force: z11.boolean().default(false)
});
var platformFeatureOverridesInput = z11.object({ overrides: z11.partialRecord(z11.enum(FEATURE_KEYS), z11.boolean().nullable()) });
var TICKET_STATUSES = ["open", "pending", "resolved", "closed"];
var TICKET_PRIORITIES = ["low", "normal", "high", "urgent"];
var platformTicketInput = z11.object({
  subject: z11.string().trim().min(3).max(160),
  body: z11.string().trim().min(3).max(4e3),
  priority: z11.enum(TICKET_PRIORITIES).default("normal")
});
var platformTicketMessageInput = z11.object({ body: z11.string().trim().min(1).max(4e3) });
var platformTicketUpdateInput = z11.object({
  status: z11.enum(TICKET_STATUSES).optional(),
  priority: z11.enum(TICKET_PRIORITIES).optional()
});
var platformSettingsInput = z11.object({
  graceDays: z11.number().int().min(0).max(90),
  pastDueDays: z11.number().int().min(0).max(60),
  suspendPolicy: z11.enum(["suspend", "notify_only"]),
  retentionDays: z11.number().int().min(0).max(3650),
  deletionWindowDays: z11.number().int().min(1).max(365),
  mrrIncludesPastDue: z11.boolean(),
  defaultPlanId: z11.uuid().nullable()
});
var platformAdminInput = z11.object({ email: emailSchema, role: z11.enum(PLATFORM_ROLES) });
var platformAdminUpdateInput = z11.object({ role: z11.enum(PLATFORM_ROLES) });
var ONBOARDING_STEP_KEYS = ["branch", "hours", "services", "barbers", "site"];
var platformOnboardingPatchInput = z11.object({ dismissed: z11.boolean() });
var platformCheckoutPortalInput = z11.object({}).strict();

// src/whatsapp.ts
import { z as z12 } from "zod";
var WHATSAPP_VARIABLES = {
  cliente: "Nome do cliente",
  servico: "Servi\xE7o agendado",
  profissional: "Profissional",
  data: "Data (dd/mm/aaaa)",
  hora: "Hor\xE1rio (hh:mm)",
  barbearia: "Nome da barbearia",
  link: "Link para gerenciar o agendamento"
};
var WHATSAPP_VARIABLE_KEYS = Object.keys(WHATSAPP_VARIABLES);
var WHATSAPP_TEMPLATE_KINDS = ["confirmation", "reminder", "cancellation"];
var WHATSAPP_TEMPLATE_LABEL = {
  confirmation: "Confirma\xE7\xE3o de agendamento",
  reminder: "Lembrete",
  cancellation: "Cancelamento"
};
var DEFAULT_WHATSAPP_TEMPLATES = {
  confirmation: "Ol\xE1, {{cliente}}! Seu agendamento na {{barbearia}} est\xE1 confirmado: {{servico}} com {{profissional}} em {{data}} \xE0s {{hora}}.\nPara ver ou cancelar: {{link}}",
  reminder: "Ol\xE1, {{cliente}}! Lembrete do seu hor\xE1rio na {{barbearia}}: {{servico}} com {{profissional}} em {{data}} \xE0s {{hora}}.\nPara remarcar ou cancelar: {{link}}",
  cancellation: "Ol\xE1, {{cliente}}. Seu agendamento de {{servico}} em {{data}} \xE0s {{hora}} na {{barbearia}} foi cancelado. Se quiser, \xE9 s\xF3 agendar um novo hor\xE1rio."
};
var WHATSAPP_TEMPLATE_MAX = 600;
var WHATSAPP_REMINDER_HOURS = [2, 4, 12, 24, 48];
var VAR_RE = /\{\{\s*([a-zA-Z_]+)\s*\}\}/g;
function unknownWhatsappVariables(text2) {
  const out = /* @__PURE__ */ new Set();
  for (const m of text2.matchAll(VAR_RE)) {
    if (!(m[1] in WHATSAPP_VARIABLES)) out.add(m[1]);
  }
  return [...out];
}
var clean = (v) => v.replace(/[\u0000-\u001f\u007f]+/g, " ").trim().slice(0, 160);
function renderWhatsappTemplate(template, values) {
  const lines = template.split("\n");
  const rendered = [];
  for (const line of lines) {
    if (!values.link && /\{\{\s*link\s*\}\}/.test(line)) continue;
    rendered.push(
      line.replace(VAR_RE, (whole, name) => {
        if (!(name in WHATSAPP_VARIABLES)) return whole;
        const v = values[name];
        return v == null ? "" : clean(String(v));
      })
    );
  }
  return rendered.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}
var templateField = z12.string().max(WHATSAPP_TEMPLATE_MAX, `M\xE1ximo de ${WHATSAPP_TEMPLATE_MAX} caracteres`).refine((v) => unknownWhatsappVariables(v).length === 0, { error: (iss) => `Vari\xE1vel n\xE3o permitida: ${unknownWhatsappVariables(String(iss.input)).map((x) => `{{${x}}}`).join(", ")}` }).transform((v) => v.trim() === "" ? null : v).nullable();
var whatsappSettingsInput = z12.object({
  enabled: z12.boolean().optional(),
  sendConfirmation: z12.boolean().optional(),
  sendReminder: z12.boolean().optional(),
  sendCancellation: z12.boolean().optional(),
  reminderHours: z12.number().int().refine((v) => WHATSAPP_REMINDER_HOURS.includes(v), "Anteced\xEAncia inv\xE1lida").optional(),
  tplConfirmation: templateField.optional(),
  tplReminder: templateField.optional(),
  tplCancellation: templateField.optional()
});
var whatsappTestInput = z12.object({
  phone: z12.string().transform((v) => v.replace(/\D/g, "")).pipe(z12.string().regex(/^\d{10,11}$/, "Telefone inv\xE1lido"))
});
var WHATSAPP_STATUSES = ["disconnected", "connecting", "connected"];
export {
  AI_UNAVAILABLE_MESSAGE,
  ALL_PERMISSIONS,
  APPOINTMENT_STATUSES,
  APPOINTMENT_TRANSITIONS,
  BILLING_EVENT_STATUSES,
  BLOCKING_STATUSES,
  DEFAULT_ROLES,
  DEFAULT_WHATSAPP_TEMPLATES,
  EMAIL_UNAVAILABLE_MESSAGE,
  FEATURE_KEYS,
  FEATURE_LABEL,
  IMPERSONATION_TTL_MIN,
  INVOICE_STATUSES,
  KPI_METRICS,
  LEGAL_VERSION,
  NOTIFICATION_CHANNELS,
  NOTIFICATION_STATUSES,
  NOTIFICATION_TYPES,
  ONBOARDING_STEP_KEYS,
  ORG_STATUSES,
  ORG_STATUS_LABEL,
  PAYMENT_KINDS,
  PERMISSIONS,
  PLAN_LIMIT_KEYS,
  PLAN_LIMIT_LABEL,
  PLATFORM_CREDENTIALS,
  PLATFORM_CREDENTIAL_LABEL,
  PLATFORM_MAX_TRIAL_DAYS,
  PLATFORM_ROLES,
  PLATFORM_ROLE_LABEL,
  PLATFORM_USER_SORTS,
  PLATFORM_USER_STATUSES,
  PLATFORM_USER_STATUS_LABEL,
  REMOVED_EMAIL_DOMAIN,
  RESERVED_PAGE_SLUGS,
  STATUS_LABELS,
  SUBSCRIPTION_ACTIONS,
  SUBSCRIPTION_STATUSES,
  SUBSCRIPTION_STATUS_LABEL,
  TICKET_PRIORITIES,
  TICKET_STATUSES,
  WHATSAPP_REMINDER_HOURS,
  WHATSAPP_STATUSES,
  WHATSAPP_TEMPLATE_KINDS,
  WHATSAPP_TEMPLATE_LABEL,
  WHATSAPP_TEMPLATE_MAX,
  WHATSAPP_UNAVAILABLE_MESSAGE,
  WHATSAPP_VARIABLES,
  WHATSAPP_VARIABLE_KEYS,
  acceptInviteInput,
  acceptTermsFlag,
  acceptTermsInput,
  addressSchema,
  aiChatInput,
  aiDailySummaryQuery,
  appointmentListQuery,
  appointmentSource,
  appointmentStatus,
  availabilityQuery,
  barberInput,
  barberServicesInput,
  barbersBlock,
  bookingPolicyInput,
  branchInput,
  businessHoursInput,
  businessHoursInterval,
  calendarQuery,
  campaignInput,
  canTransition,
  cancelSubscriptionInput,
  cashMovementInput,
  cashRegisterExportQuery,
  cashRegisterListQuery,
  cents,
  cepBR,
  cepLookupResponse,
  chairInput,
  changePasswordInput,
  changeStatusInput,
  closeCashRegisterInput,
  cnpjBR,
  commissionListQuery,
  commissionRuleInput,
  contactFormBlock,
  contactMessageInput,
  couponInput,
  couponListQuery,
  cpfBR,
  createAppointmentInput,
  createPageInput,
  ctaBlock,
  customerInactiveQuery,
  customerInput,
  customerListQuery,
  emailSchema,
  expenseInput,
  expenseListQuery,
  faqBlock,
  forgotPasswordInput,
  galleryBlock,
  heroBlock,
  holidayInput,
  idempotencyKey,
  idempotencyKeyHeader,
  internalAvailabilityQuery,
  inviteMemberInput,
  isValidCnpj,
  isValidCpf,
  isoDate,
  isoDateTime,
  kpiMetric,
  locationBlock,
  loginInput,
  loyaltyAdjustInput,
  loyaltyProgramInput,
  messagesQuery,
  notificationListQuery,
  onboardingStatus,
  onlyDigits,
  openCashRegisterInput,
  optionalText,
  organizationSettingsInput,
  pageContentSchema,
  pageSlug,
  paginationQuery,
  passwordSchema,
  payCommissionsInput,
  paymentInput,
  paymentKind,
  paymentListQuery,
  paymentMethodInput,
  paymentQuoteInput,
  pendingPaymentsQuery,
  phoneBR,
  platformAdminInput,
  platformAdminUpdateInput,
  platformAuditQuery,
  platformCancelSubscriptionInput,
  platformChangePlanInput,
  platformCheckoutPortalInput,
  platformCreateOrgInput,
  platformCreateOrgUserInput,
  platformEventsQuery,
  platformExtendTrialInput,
  platformFeatureFlagInput,
  platformFeatureOverridesInput,
  platformImpersonateInput,
  platformInvoicesQuery,
  platformListQuery,
  platformMemberUpdateInput,
  platformMetricsQuery,
  platformOnboardingPatchInput,
  platformOrgsQuery,
  platformPlanInput,
  platformReactivateSubscriptionInput,
  platformScheduleDeletionInput,
  platformSettingsInput,
  platformSubscriptionsQuery,
  platformSuspendOrgInput,
  platformTicketInput,
  platformTicketMessageInput,
  platformTicketUpdateInput,
  platformTicketsQuery,
  platformUsageQuery,
  platformUserDeleteInput,
  platformUserSetPasswordInput,
  platformUserUpdateInput,
  platformUsersQuery,
  publicAssistantInput,
  publicAvailabilityQuery,
  publicBookingInput,
  publicCancelInput,
  redeemRewardInput,
  refundInput,
  registerInput,
  renderWhatsappTemplate,
  reportQuery,
  rescheduleAppointmentInput,
  resetPasswordInput,
  reviewInput,
  reviewPatchInput,
  reviewsBlock,
  reviewsQuery,
  roleInput,
  safeUrl,
  saveDraftInput,
  scheduleBlockInput,
  scheduleBlockListQuery,
  seoSchema,
  serviceCategoryInput,
  serviceInput,
  serviceListQuery,
  servicesBlock,
  signedCents,
  siteBlock,
  siteThemeInput,
  slugSchema,
  sortOrder,
  subscribeCustomerInput,
  subscriptionPlanInput,
  textBlock,
  timeHHmm,
  unknownWhatsappVariables,
  updateMemberInput,
  updatePageMetaInput,
  updateProfileInput,
  uuid,
  waitlistInput,
  waitlistListQuery,
  waitlistStatusInput,
  weekday,
  whatsappSettingsInput,
  whatsappTestInput
};
